import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from "react-router-dom";
import toast from 'react-hot-toast';
import { 
  useTheme, 
  useMediaQuery, 
  Box,
  Typography, 
  CircularProgress, 
  Tooltip, 
  IconButton,
} from '@mui/material';
import {
  AddCircleOutline,
  Edit,
  Update,
  Delete,
  DeleteForever,
} from '@mui/icons-material';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import useAuthCheck from 'hooks/useAuthCheck';
import api from 'state/axios';
import { LAYOUT_HEIGHT } from 'state/constants';
import { hasPermission } from 'state/checkPermission';
import { getSelectedIds, isAnyRowSelected } from 'state/helpers';
import DataGridComponent from 'components/DataGridComponent';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import Header from 'components/Header';
import StyledButton from 'components/StyledButton';
import DeleteConfirm from 'components/DeleteConfirm';
import CardList from 'components/CardList';


const CrewScheduleCodes = () => {
  useAuthCheck();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user);
  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const canCreate = hasPermission(user?.permissions, "CrewScheduleCodes", "create", false);
  const canEdit   = hasPermission(user?.permissions, "CrewScheduleCodes", "update", false);
  const canDelete = hasPermission(user?.permissions, "CrewScheduleCodes", "delete", false);

  const { startDate, endDate } = useDateRangeContext();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [crewScheduleCodes, setCrewScheduleCodes] = useState([]);
  const [rowSelectionModel, setRowSelectionModel] = useState({
    type: 'include',
    ids: new Set(),
  });

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [deleteType, setDeleteType] = useState(null);
  const [idToDelete, setIdToDelete] = useState(null);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({ _id: false });


  useEffect(() => {
    const fetchCrewScheduleCodes = async () => {
      try {
        setError(null);
        setLoading(true);
        const res = await api.get('/crewschedulecodes');
        setCrewScheduleCodes(res.data);
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    };

    fetchCrewScheduleCodes();
  }, []);

  const columns = useMemo(() => [
    {
      field: "no",
      headerName: "No",
      filterable: false,
      sortable: false,
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1,
      ...isMobile ? { width: 60 } : { flex: 1 },
    },
    {
      field: "code",
      headerName: "Code",
      ...isMobile ? { width: 80 } : { flex: 1.5 },
    },
    {
      field: "description",
      headerName: "Description",
      ...isMobile ? { width: 300 } : { flex: 5 },
    },
    {
      field: "category",
      headerName: "Category",
      ...isMobile ? { width: 120 } : { flex: 1.8 },
    },
    {
      field: "isTimesheet",
      headerName: "IsTimesheet",
      ...isMobile ? { width: 80 } : { flex: 1.5 },
    },
    {
      field: "workingDays",
      headerName: "WorkingDays",
      align: "right",
      ...isMobile ? { width: 80 } : { flex: 1.5 },
    },
    {
      field: "isPaid",
      headerName: "IsPaid",
      ...isMobile ? { width: 80 } : { flex: 1.5 },
    },
    {
      field: "isCrewRequest",
      headerName: "IsCrewRequest",
      ...isMobile ? { width: 80 } : { flex: 1.5 },
    },
  ], [isMobile]);

  const actionColumn = useMemo(() => {
    const activeCount = [canEdit, canDelete].filter(Boolean).length;
    if (activeCount === 0) return [];

    const baseWidth = 60 + activeCount * 25;

    return [
      {
        field: "action",
        headerName: "Action",
        filterable: false,
        sortable: false,
        width: baseWidth,
        renderCell: (params) => (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            gap={1}
            sx={{ width: '100%', height: '100%' }}
          >
            {canEdit && (
              <Tooltip title="Edit">
                <IconButton
                  aria-label="Edit"
                  sx={{
                    color: theme.palette.primary.dark,
                    '&:hover': { color: theme.palette.green.deep },
                  }}
                  onClick={() => navigate(`/crewschedulecodes/${params.row._id}`)}
                >
                  <Edit />
                </IconButton>
              </Tooltip>
            )}
            {canDelete && (
              <Tooltip title="Delete">
                <IconButton
                  aria-label="Delete"
                  sx={{
                    color: theme.palette.primary.dark,
                    '&:hover': { color: theme.palette.green.deep },
                  }}
                  onClick={() => handleDeleteClick(params.row._id)}
                >
                  <DeleteForever />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        ),
      },
    ];
  }, [navigate, theme.palette, canEdit, canDelete]);

  const handleUpdate = useCallback(async () => {
    try {
      const res = await api.post('/crewschedulecodes/bulk-update', { startDate, endDate });
      if (res.data?.success) {
        setCrewScheduleCodes(res.data.data);
        toast.success(
          `Updated successfully: ${res.data.inserted} new / ${res.data.matched} existing.`
        );
      }
    } catch (error) {
      toast.error(error?.message || "Failed to update crew schedule codes!");
    }
  }, [startDate, endDate]);

  const handleDeleteClick = (id) => {
    setIdToDelete(id);
    setDeleteType('single');
    setConfirmDialogOpen(true);
  };

  const handleBulkDelete = () => {
    const selectedIds = getSelectedIds(rowSelectionModel, crewScheduleCodes);
    if (selectedIds.length === 0) {
      toast.error('No rows selected for deletion!');
      return;
    }
    setDeleteType('bulk');
    setConfirmDialogOpen(true);
  };

  const executeDelete = useCallback(async () => {
    try {
      if (!canDelete) return;

      if (deleteType === 'single') {
        if (!idToDelete) return;
        await api.delete(`/crewschedulecodes/delete/${idToDelete}`);
        setCrewScheduleCodes(prev => prev.filter(item => item._id !== idToDelete));
        toast.success('Crew schedule code deleted successfully.');
      } else if (deleteType === 'bulk') {
        const selectedIds = getSelectedIds(rowSelectionModel, crewScheduleCodes);
        if (selectedIds.length === 0) {
          toast.error('No rows selected for deletion!');
          return;
        }
        const res = await api.patch('/crewschedulecodes/soft-delete', { ids: selectedIds });
        toast.success(res.data?.message || `${selectedIds.length} record(s) deleted successfully.`);
        setCrewScheduleCodes(prev => prev.filter(item => !selectedIds.includes(item._id)));
      }
    } catch (err) {
      toast.error(err.message || "Failed to delete crew schedule codes!");
    }
  }, [canDelete, deleteType, idToDelete, rowSelectionModel, crewScheduleCodes]);


  return (
    <Box className="main-container-box">
      <FlexBetween>
        <Header
          title="Crew Schedule Codes"
          subtitle={loading ? 'Loading...' : 'List of Crew Schedule Codes'}
        />
        {!loading && (
          <Box display="flex" alignItems="center" gap={1}>
            {canEdit && (
              <Tooltip title="Update from roster details" arrow>
                <span>
                  <StyledButton onClick={handleUpdate}>
                    <Update />
                  </StyledButton>
                </span>
              </Tooltip>
            )}
            {!isMobile && canDelete && (
              <Tooltip title="Bulk soft delete" arrow>
                <span>
                  <StyledButton
                    disabled={!isAnyRowSelected(rowSelectionModel, crewScheduleCodes)}
                    onClick={handleBulkDelete}
                  >
                    <Delete />
                  </StyledButton>
                </span>
              </Tooltip>
            )}
            {canCreate && (
              <Tooltip title="Create" arrow>
                <span>
                  <StyledButton onClick={() => navigate("/crewschedulecodes/new")}>
                    <AddCircleOutline />
                  </StyledButton>
                </span>
              </Tooltip>
            )}
          </Box>
        )}
      </FlexBetween>

      {loading ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <CircularProgress />
        </FlexCenter>
      ) : error ? (
        <FlexCenter height={LAYOUT_HEIGHT}>
          <Typography color="error">Error: {error?.message || ''}</Typography>
        </FlexCenter>
      ) : (
        <Box
          display="grid"
          gap={2}
          gridTemplateColumns="repeat(12, minmax(0, 1fr))"
        >
          <Box height={LAYOUT_HEIGHT} sx={{ gridColumn: "span 12" }}>
            {isMobile ? (
              <CardList
                rows={crewScheduleCodes}
                primaryField="code"
                fields={[
                  { label: "Description", key: "description" },
                  { label: "Category", key: "category" },
                  { label: "Is Timesheet", key: "isTimesheet" },
                  { label: "Working Days", key: "workingDays" },
                  { label: "Is Paid", key: "isPaid" },
                  { label: "Is Request", key: "isCrewRequest" },
                ]}
                canEdit={canEdit}
                canDelete={canDelete}
                onEdit={(id) => navigate(`/crewschedulecodes/${id}`)}
                onDelete={handleDeleteClick}
              />
            ) : (
              <DataGridComponent
                rows={crewScheduleCodes || []}
                columns={columns.concat(actionColumn)}
                columnVisibilityModel={columnVisibilityModel}
                setColumnVisibilityModel={setColumnVisibilityModel}
                loading={loading}
                props={{
                  checkboxSelection: true,
                  disableRowSelectionOnClick: true,
                  onRowSelectionModelChange: setRowSelectionModel,
                  rowSelectionModel,
                  initialState: { pagination: { paginationModel: { pageSize: 100 } } },
                }}
              />
            )}
          </Box>
        </Box>
      )}

      <DeleteConfirm
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={executeDelete}
        deleteType={deleteType}
      />
    </Box>
  );
};

export default CrewScheduleCodes;
